import { Head, Link, router, usePage } from '@inertiajs/react'
import { ArrowLeft, MapPin, DollarSign, Mail, Phone, Globe, Bookmark, BookmarkCheck, Users, Ruler, Building2, Clock, Briefcase, Play, FileText } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import type { EventService, SharedProps } from '@/types'

type Props = {
  service: EventService
  is_saved: boolean
}

export default function EventServiceShow({ service, is_saved }: Props) {
  const { current_organization: currentOrg, app_url } = usePage<SharedProps>().props
  const ogImage = service.cover_image_url || service.logo_url || `${app_url}/meta.png`
  const ogUrl = `${app_url}/services/${service.slug}`
  const ogDescription = service.description ? service.description.slice(0, 160) : `${service.name} — event services on Gala Season.`
  const location = [service.city, service.state].filter(Boolean).join(', ')
  const fullAddress = !service.hide_address
    ? [service.street_address, service.city, service.state, service.zip].filter(Boolean).join(', ')
    : location

  const socialLinks = [
    { url: service.instagram_url, label: 'Instagram' },
    { url: service.twitter_url, label: 'X / Twitter' },
    { url: service.facebook_url, label: 'Facebook' },
    { url: service.linkedin_url, label: 'LinkedIn' },
    { url: service.youtube_url, label: 'YouTube' },
    { url: service.tiktok_url, label: 'TikTok' },
  ].filter(l => l.url)

  function toggleSave() {
    if (is_saved) {
      router.delete(`/services/${service.slug}/save`, { preserveScroll: true })
    } else {
      router.post(`/services/${service.slug}/save`, {}, { preserveScroll: true })
    }
  }

  return (
    <>
      <Head title={`${service.name} — Event Services`}>
        <meta head-key="description" name="description" content={ogDescription} />
        <meta head-key="og:title" property="og:title" content={`${service.name} — Gala Season`} />
        <meta head-key="og:description" property="og:description" content={ogDescription} />
        <meta head-key="og:type" property="og:type" content="website" />
        <meta head-key="og:url" property="og:url" content={ogUrl} />
        <meta head-key="og:image" property="og:image" content={ogImage} />
        <meta head-key="twitter:card" name="twitter:card" content="summary_large_image" />
        <meta head-key="twitter:title" name="twitter:title" content={`${service.name} — Gala Season`} />
        <meta head-key="twitter:description" name="twitter:description" content={ogDescription} />
        <meta head-key="twitter:image" name="twitter:image" content={ogImage} />
      </Head>

      <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6 lg:px-8">
        <Link
          href="/services"
          className="mb-6 inline-flex items-center gap-1 text-base text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="size-3.5" />
          Back to Directory
        </Link>

        {/* Hero */}
        {service.cover_image_url ? (
          <div className="relative h-64 overflow-hidden rounded-2xl sm:h-80">
            <img src={service.cover_image_url} alt={service.name} className="h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
          </div>
        ) : (
          <div className="relative flex h-48 items-center justify-center rounded-2xl bg-gradient-to-b from-[oklch(0.25_0.02_50)] via-[oklch(0.35_0.10_46)] to-primary sm:h-64">
            {service.logo_url && (
              <img src={service.logo_url} alt={service.name} className="max-h-24 max-w-48 object-contain" />
            )}
          </div>
        )}

        {/* Logo overlay */}
        {service.logo_url && service.cover_image_url && (
          <div className="relative -mt-10 ml-6 inline-block">
            <div className="rounded-xl bg-white p-2 shadow-md ring-1 ring-black/5">
              <img src={service.logo_url} alt={`${service.name} logo`} className="h-16 w-auto object-contain" />
            </div>
          </div>
        )}

        <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-3">
          {/* Main content */}
          <div className="lg:col-span-2">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h1 className="text-4xl font-semibold tracking-tight font-serif">{service.name}</h1>
                <p className="mt-1 text-muted-foreground">{service.service_type}</p>
              </div>
              {currentOrg && (
                <Button
                  variant={is_saved ? 'default' : 'outline'}
                  size="sm"
                  onClick={toggleSave}
                >
                  {is_saved ? <BookmarkCheck className="size-4 mr-1" /> : <Bookmark className="size-4 mr-1" />}
                  {is_saved ? 'Saved' : 'Save'}
                </Button>
              )}
            </div>

            <div className="mt-3 flex flex-wrap items-center gap-3">
              {location && (
                <span className="inline-flex items-center gap-1 text-base text-muted-foreground">
                  <MapPin className="size-3.5" /> {location}
                </span>
              )}
              {service.price_range && (
                <span className="inline-flex items-center gap-1 text-base text-muted-foreground">
                  <DollarSign className="size-3.5" /> {service.price_range}
                </span>
              )}
              {service.years_experience && (
                <span className="inline-flex items-center gap-1 text-base text-muted-foreground">
                  <Clock className="size-3.5" /> {service.years_experience}+ years experience
                </span>
              )}
            </div>

            {service.description && (
              <div className="mt-8">
                <h2 className="text-xl font-semibold tracking-tight font-serif">About</h2>
                <div className="mt-3 whitespace-pre-wrap text-foreground leading-relaxed">
                  {service.description}
                </div>
              </div>
            )}

            {/* Venue details */}
            {service.service_type === 'Venue' && (service.capacity || service.seated_capacity || service.standing_capacity || service.square_footage || service.floors) && (
              <div className="mt-8">
                <h2 className="text-xl font-semibold tracking-tight font-serif">Venue Details</h2>
                <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3">
                  {service.capacity && (
                    <div className="rounded-xl bg-muted/50 p-4">
                      <div className="flex items-center gap-2 text-base text-muted-foreground"><Users className="size-4" /> Total Capacity</div>
                      <p className="mt-1 text-xl font-semibold">{service.capacity.toLocaleString()}</p>
                    </div>
                  )}
                  {service.seated_capacity && (
                    <div className="rounded-xl bg-muted/50 p-4">
                      <div className="flex items-center gap-2 text-base text-muted-foreground"><Users className="size-4" /> Seated</div>
                      <p className="mt-1 text-xl font-semibold">{service.seated_capacity.toLocaleString()}</p>
                    </div>
                  )}
                  {service.standing_capacity && (
                    <div className="rounded-xl bg-muted/50 p-4">
                      <div className="flex items-center gap-2 text-base text-muted-foreground"><Users className="size-4" /> Standing</div>
                      <p className="mt-1 text-xl font-semibold">{service.standing_capacity.toLocaleString()}</p>
                    </div>
                  )}
                  {service.square_footage && (
                    <div className="rounded-xl bg-muted/50 p-4">
                      <div className="flex items-center gap-2 text-base text-muted-foreground"><Ruler className="size-4" /> Square Footage</div>
                      <p className="mt-1 text-xl font-semibold">{service.square_footage.toLocaleString()} sq ft</p>
                    </div>
                  )}
                  {service.floors && (
                    <div className="rounded-xl bg-muted/50 p-4">
                      <div className="flex items-center gap-2 text-base text-muted-foreground"><Building2 className="size-4" /> Floors</div>
                      <p className="mt-1 text-xl font-semibold">{service.floors}</p>
                    </div>
                  )}
                  {service.private_rooms && (
                    <div className="rounded-xl bg-muted/50 p-4">
                      <div className="flex items-center gap-2 text-base text-muted-foreground"><Building2 className="size-4" /> Private Rooms</div>
                      <p className="mt-1 text-base font-medium">{service.private_rooms}</p>
                    </div>
                  )}
                </div>
                {service.floor_plan_url && (
                  <a href={service.floor_plan_url} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex items-center gap-1.5 text-base text-primary hover:underline">
                    <FileText className="size-4" /> View Floor Plan
                  </a>
                )}
              </div>
            )}

            {/* Additional services */}
            {service.additional_services && service.additional_services.length > 0 && (
              <div className="mt-8">
                <h2 className="text-xl font-semibold tracking-tight font-serif">Additional Services</h2>
                <div className="mt-3 flex flex-wrap gap-2">
                  {service.additional_services.map(svc => (
                    <Badge key={svc} variant="secondary">{svc}</Badge>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Contact */}
            <div className="rounded-2xl bg-muted/30 ring-1 ring-black/5 p-5">
              <h3 className="font-semibold tracking-tight">Contact Information</h3>
              <div className="mt-4 space-y-3">
                {service.contact_email && (
                  <a href={`mailto:${service.contact_email}`} className="flex items-center gap-2 text-base text-foreground hover:text-primary">
                    <div className="rounded-lg bg-primary/10 p-2"><Mail className="size-4 text-primary" /></div>
                    {service.contact_email}
                  </a>
                )}
                {service.phone && (
                  <a href={`tel:${service.phone}`} className="flex items-center gap-2 text-base text-foreground hover:text-primary">
                    <div className="rounded-lg bg-primary/10 p-2"><Phone className="size-4 text-primary" /></div>
                    {service.phone}
                  </a>
                )}
                {service.website && (
                  <a href={service.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-base text-foreground hover:text-primary">
                    <div className="rounded-lg bg-primary/10 p-2"><Globe className="size-4 text-primary" /></div>
                    Website
                  </a>
                )}
                {service.portfolio_url && (
                  <a href={service.portfolio_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-base text-foreground hover:text-primary">
                    <div className="rounded-lg bg-primary/10 p-2"><Briefcase className="size-4 text-primary" /></div>
                    Portfolio
                  </a>
                )}
                {service.video_url && (
                  <a href={service.video_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-base text-foreground hover:text-primary">
                    <div className="rounded-lg bg-primary/10 p-2"><Play className="size-4 text-primary" /></div>
                    Video
                  </a>
                )}
              </div>
            </div>

            {/* Address */}
            {fullAddress && (
              <div className="rounded-2xl bg-muted/30 ring-1 ring-black/5 p-5">
                <h3 className="font-semibold tracking-tight">Location</h3>
                <p className="mt-2 text-base text-foreground">{fullAddress}</p>
              </div>
            )}

            {/* Social */}
            {socialLinks.length > 0 && (
              <div className="rounded-2xl bg-muted/30 ring-1 ring-black/5 p-5">
                <h3 className="font-semibold tracking-tight">Social Media</h3>
                <div className="mt-3 flex flex-wrap gap-2">
                  {socialLinks.map(({ url, label }) => (
                    <a
                      key={label}
                      href={url!}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Badge variant="secondary">{label}</Badge>
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  )
}
